import { styled } from "@material-ui/core";
import { observer } from "mobx-react";
import * as React from "react";
import Localization from "../../core/Localization";
import Sys from "../../core/Sys";
import Button from "../../coreui/Button";
import Dialog, { BreakPointColumn } from "../../coreui/Dialog";
import DialogActions from "../../coreui/DialogActions";
import DialogContent from "../../coreui/DialogContent";
import Markdown, { Justification } from "../../coreui/Markdown";
import { TableChildProps } from "../../coreui/Table";
import {
  CellFocusUtil,
  FocusCellRendererParams,
} from "../../coreui/table/CellFocusUtil";
import PaneRow, { RuntimeWidget, WidgetValue } from "../../models/PaneRow";
import { AccessLevel } from "../Api";
import { FunctionName } from "../TableSummary";
import { GridColumnConfigProperties } from "./GridColumn";

interface ConfigProperties extends FocusCellRendererParams {
  dataId: string;
  name: string;
  dialogType: string;
  justification: Justification;
  children: React.ReactNode;
  labelledById: string;
  onClose: () => void;
  onExited?: () => void;
  onOpen: (parentRowKey: string) => Promise<BreakPointColumn[]>;
  parentRowKey?: string;
  propagated: TableChildProps;
}

interface RuntimeProperties {
  accessLevel: AccessLevel;
  textValue: string;
}

const dialogTypeButtonConfig = {
  "Help Dialog": {
    color: "information",
    icon: "fas fa-question",
  },
  "Info Dialog": {
    color: "information",
    icon: "fas fa-info",
  },
  "Warning Dialog": {
    color: "warning",
    icon: "fas fa-exclamation",
  },
};

const widgetType = "MarkdownDialogColumn";

function getAccessibleButtonText(dialogType: string): string {
  switch (dialogType) {
    case "Info Dialog":
      return Localization.getBuiltInMessage("DataTable.viewInfo");
    case "Help Dialog":
      return Localization.getBuiltInMessage("DataTable.viewHelp");
    case "Warning Dialog":
      return Localization.getBuiltInMessage("DataTable.viewWarning");
    default:
      return "";
  }
}

function getFilterText(
  column: GridColumnConfigProperties,
  propagated: TableChildProps,
  row: PaneRow
): string {
  return "";
}

function getSummaryValue(
  runtimeData: RuntimeWidget[],
  configProperties: ConfigProperties,
  functionName: FunctionName
): string | null {
  let result = 0;
  for (const data of runtimeData) {
    const runtimeProperties = data.properties as RuntimeProperties;

    if (runtimeProperties.textValue) {
      result += 1;
    }
  }

  return result.toString();
}

const DialogButtonDiv = styled("div")({
  alignItems: "center",
  display: "flex",
  height: "100%",
  justifyContent: "center",
});

const MarkdownDialogColumn = observer(
  (props: ConfigProperties): JSX.Element | null => {
    const [open, setOpen] = React.useState(false);
    const componentId = React.useRef<string>(
      `markdown-dialog-column-${Sys.nextId}`
    );

    const row = props["data"] as PaneRow;
    const widget = row.getWidgetT<WidgetValue, RuntimeProperties>(props.name);
    const runtimeProperties = widget.properties;

    const handleCellFocus = () => undefined;
    const handleCellBlur = () => undefined;

    const handleOpen = () => {
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
    };

    const handleCellKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        handleOpen();
      }
    };

    const handleDialogKeyDown = (
      event: React.KeyboardEvent<HTMLDivElement>
    ) => {
      if (
        event.key === "Escape" ||
        ((event.key === "Enter" || event.key === " ") &&
          "nodeName" in event.target &&
          event.target["nodeName"] === "BUTTON")
      ) {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };

    React.useEffect(() => {
      CellFocusUtil.subscribeToCellKeyboardFocusedEvent(
        props,
        handleCellFocus,
        handleCellBlur
      );
      props.eGridCell.addEventListener("keydown", handleCellKeyDown);
      return () => {
        CellFocusUtil.unsubscribeToCellKeyboardFocusedEvent(
          props,
          handleCellFocus,
          handleCellBlur
        );
        props.eGridCell.removeEventListener("keydown", handleCellKeyDown);
      };
    }, []);

    if (runtimeProperties.accessLevel === AccessLevel.hidden) {
      return null;
    }

    return (
      <>
        <DialogButtonDiv>
          <Button
            aria-label={getAccessibleButtonText(props.dialogType)}
            onClick={handleOpen}
            size="small"
            tabIndex={-1}
            {...dialogTypeButtonConfig[props.dialogType]}
          />
        </DialogButtonDiv>
        <Dialog
          open={open}
          onClose={handleClose}
          onKeyDown={handleDialogKeyDown}
          fullWidth
        >
          <DialogContent>
            <Markdown
              autoFocus
              justification={{
                lg: props.justification,
                md: props.justification,
                sm: props.justification,
                xl: props.justification,
                xs: props.justification,
              }}
              value={runtimeProperties.textValue}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>
              {Localization.getBuiltInMessage("close")}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export { getFilterText, getSummaryValue, MarkdownDialogColumn, widgetType };
